import React, { useState } from 'react';
import { Phone, Mail, MapPin, Calendar } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from './ui/sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    junkType: '',
    estimatedVolume: '',
    preferredDate: '',
    message: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Frontend validation
    if (!formData.name || !formData.phone || !formData.email) {
      toast.error("Missing Information", {
        description: "Please fill in all required fields (Name, Phone, Email)."
      });
      return;
    }

    try {
      // Send to backend
      const response = await axios.post(`${API}/quote`, {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        address: formData.address,
        junk_type: formData.junkType,
        estimated_volume: formData.estimatedVolume,
        preferred_date: formData.preferredDate,
        message: formData.message
      });

      // Show success message
      toast.success("Quote Request Received!", {
        description: `Thank you, ${formData.name}! We'll contact you shortly at ${formData.phone}.`
      });

      // Reset form
      setFormData({
        name: '',
        phone: '',
        email: '',
        address: '',
        junkType: '',
        estimatedVolume: '',
        preferredDate: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error("Submission Failed", {
        description: "There was an error submitting your quote. Please try calling us directly."
      });
    }
  };

  return (
    <section id="contact" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Get Your Free Quote Today
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Fill out the form below or give us a call. We'll get back to you with a free estimate within 24 hours.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Information */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="border-2 border-emerald-100">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-gray-900">Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-emerald-100 p-3 rounded-lg flex-shrink-0">
                    <Phone className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Phone</h3>
                    <a href="tel:3653363364" className="text-emerald-600 hover:text-emerald-700 font-semibold">
                      (365) 336-3364
                    </a>
                    <p className="text-sm text-gray-600 mt-1">Mon-Sat: 7am - 7pm</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-emerald-100 p-3 rounded-lg flex-shrink-0">
                    <Mail className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                    <a href="mailto:Info.junkio@gmail.com" className="text-emerald-600 hover:text-emerald-700">
                      Info.junkio@gmail.com
                    </a>
                    <p className="text-sm text-gray-600 mt-1">We'll respond within 24 hours</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-emerald-100 p-3 rounded-lg flex-shrink-0">
                    <MapPin className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Service Area</h3>
                    <p className="text-gray-700">Hamilton, Ontario</p>
                    <p className="text-sm text-gray-600 mt-1">& Surrounding Areas</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className="bg-emerald-600 text-white p-6 rounded-xl">
              <h3 className="text-xl font-bold mb-3">Same-Day Service Available!</h3>
              <p className="mb-4">Need immediate junk removal? Give us a call and we'll do our best to accommodate same-day requests.</p>
              <a href="tel:3653363364">
                <Button className="w-full bg-white text-emerald-600 hover:bg-gray-100 font-semibold py-6">
                  Call for Same-Day Service
                </Button>
              </a>
            </div>
          </div>

          {/* Quote Form */}
          <div className="lg:col-span-2">
            <Card className="border-2 border-emerald-100 overflow-visible">
              <CardHeader>
                <CardTitle className="text-2xl font-bold text-gray-900">Request a Free Quote</CardTitle>
              </CardHeader>
              <CardContent className="overflow-visible">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-gray-900 font-semibold mb-2 block">
                        Full Name <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
                      />
                    </div>

                    <div>
                      <Label htmlFor="phone" className="text-gray-900 font-semibold mb-2 block">
                        Phone Number <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="(365) 336-3364"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-gray-900 font-semibold mb-2 block">
                      Email Address <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>

                  <div>
                    <Label htmlFor="address" className="text-gray-900 font-semibold mb-2 block">
                      Service Address
                    </Label>
                    <Input
                      id="address"
                      name="address"
                      type="text"
                      placeholder="123 Main St, Hamilton, ON"
                      value={formData.address}
                      onChange={handleChange}
                      className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="junkType" className="text-gray-900 font-semibold mb-2 block">
                        Type of Junk
                      </Label>
                      <select
                        id="junkType"
                        name="junkType"
                        value={formData.junkType}
                        onChange={handleChange}
                        className="w-full h-11 px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 appearance-none cursor-pointer"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                          backgroundPosition: 'right 0.5rem center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: '1.5em 1.5em',
                          paddingRight: '2.5rem'
                        }}
                      >
                        <option value="" disabled>Select type</option>
                        <option value="residential">Residential Items</option>
                        <option value="commercial">Commercial Items</option>
                        <option value="furniture">Furniture & Appliances</option>
                        <option value="construction">Construction Debris</option>
                        <option value="yard">Yard Waste</option>
                        <option value="estate">Estate Cleanout</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <Label htmlFor="estimatedVolume" className="text-gray-900 font-semibold mb-2 block">
                        Estimated Volume
                      </Label>
                      <select
                        id="estimatedVolume"
                        name="estimatedVolume"
                        value={formData.estimatedVolume}
                        onChange={handleChange}
                        className="w-full h-11 px-3 py-2 border border-gray-300 rounded-md bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 appearance-none cursor-pointer"
                        style={{
                          backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
                          backgroundPosition: 'right 0.5rem center',
                          backgroundRepeat: 'no-repeat',
                          backgroundSize: '1.5em 1.5em',
                          paddingRight: '2.5rem'
                        }}
                      >
                        <option value="" disabled>Select volume</option>
                        <option value="small">Small (1/4 truck)</option>
                        <option value="medium">Medium (1/2 truck)</option>
                        <option value="large">Large (3/4 truck)</option>
                        <option value="full">Full Load (14 yards)</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="preferredDate" className="text-gray-900 font-semibold mb-2 block">
                      Preferred Date
                    </Label>
                    <Input
                      id="preferredDate"
                      name="preferredDate"
                      type="date"
                      value={formData.preferredDate}
                      onChange={handleChange}
                      className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-gray-900 font-semibold mb-2 block">
                      Additional Details
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Tell us more about your junk removal needs..."
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500 resize-none"
                    />
                  </div>

                  <Button 
                    type="submit"
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-lg font-semibold transition-colors duration-300"
                  >
                    Get Your Free Quote
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;